import { buildBriefingData } from './buildConsultationBriefing';
import { readExactAppointmentV2 } from './appointmentEvidence';

const DAY_MS = 86_400_000;

const toTime = (value) => {
  const time = Date.parse(value);
  return Number.isFinite(time) ? time : null;
};

const toIso = (value) => {
  const time = toTime(value);
  return time == null ? null : new Date(time).toISOString();
};

const eventTime = (value) => toTime(
  value?.consultedAt
  || value?.created_at
  || value?.createdAt
  || value?.date
  || value?.sent_at
  || value?.submitted_at
  || value?.published_at,
);

const inWindow = (value, from, to) => {
  const time = toTime(value);
  return time != null && time >= from && time <= to;
};

const mondayStart = (time) => {
  const date = new Date(time);
  date.setUTCHours(0, 0, 0, 0);
  const weekday = (date.getUTCDay() + 6) % 7;
  return date.getTime() - (weekday * DAY_MS);
};

const formatDay = (time) => new Date(time).toLocaleDateString('fr-CH', {
  day: '2-digit',
  month: 'short',
});

const feedbackEventAt = (row) => (
  row?.updated_at
  || row?.created_at
  || (typeof row?.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(row.date)
    ? `${row.date}T12:00:00.000Z`
    : row?.date)
);

const REVIEWED_WORKFLOW_ACTIONS = new Set(['continue', 'adapt', 'transition', 'newVersion']);

function readReviewedAppointment(consultation) {
  const clinical = consultation?.clinical;
  if (!clinical || typeof clinical !== 'object' || Array.isArray(clinical)) return null;
  const keys = Object.keys(clinical).sort();
  if (keys.length !== 2 || keys[0] !== 'appointment' || keys[1] !== 'workflow_action'
      || !REVIEWED_WORKFLOW_ACTIONS.has(clinical.workflow_action)) return null;
  const appointment = readExactAppointmentV2(clinical.appointment);
  return appointment ? {
    appointment,
    cycleProof: appointment.cycleReview,
    workflowAction: clinical.workflow_action,
  } : null;
}

function readCycleProof(consultation) {
  return readReviewedAppointment(consultation)?.cycleProof || null;
}

function sameCycleReview(review, proof) {
  return Boolean(
    review
    && proof
    && review.id === proof.id
    && review.step_number === proof.step_number
    && review.template_type === proof.template_type,
  );
}

export function resolveLongitudinalAnchor({
  consultationsLog = [],
  cycleReviews = [],
  versions = [],
  packStartedAt = null,
  firstDataAt = null,
  nowISO = new Date().toISOString(),
} = {}) {
  const now = toTime(nowISO) ?? Date.now();
  const consultations = (Array.isArray(consultationsLog) ? consultationsLog : [])
    .map((consultation) => ({ consultation, time: eventTime(consultation) }))
    .filter(({ time }) => time != null && time <= now)
    .sort((a, b) => b.time - a.time);

  if (consultations.length > 0) {
    const latest = consultations[0];
    const proof = readCycleProof(latest.consultation);
    const review = proof
      ? (Array.isArray(cycleReviews) ? cycleReviews : []).find((row) => sameCycleReview(row, proof))
      : null;
    return {
      at: new Date(latest.time).toISOString(),
      kind: review ? 'cycle_review' : 'consultation',
      label: review ? `Depuis le bilan S${proof.step_number}` : 'Depuis votre dernier suivi',
      consultationId: latest.consultation?.id || latest.consultation?.source_consultation_id || null,
      cycleReviewId: review?.id || null,
    };
  }

  const publications = (Array.isArray(versions) ? versions : [])
    .map((version) => ({
      version,
      time: toTime(version?.client_app_publication?.published_at || version?.published_at),
    }))
    .filter(({ time }) => time != null && time <= now)
    .sort((a, b) => b.time - a.time);
  if (publications.length > 0) {
    return {
      at: new Date(publications[0].time).toISOString(),
      kind: 'publication',
      label: 'Depuis la dernière publication',
      consultationId: null,
      cycleReviewId: null,
    };
  }

  const packTime = toTime(packStartedAt);
  if (packTime != null && packTime <= now) {
    return {
      at: new Date(packTime).toISOString(),
      kind: 'pack_start',
      label: "Depuis le début de l'accompagnement",
      consultationId: null,
      cycleReviewId: null,
    };
  }

  const firstTime = toTime(firstDataAt);
  if (firstTime != null && firstTime <= now) {
    return {
      at: new Date(firstTime).toISOString(),
      kind: 'first_data',
      label: 'Depuis les premières données',
      consultationId: null,
      cycleReviewId: null,
    };
  }

  return {
    at: new Date(now - (35 * DAY_MS)).toISOString(),
    kind: 'fallback',
    label: 'Sur la période récente',
    consultationId: null,
    cycleReviewId: null,
  };
}

export function buildLongitudinalPeriod(anchor, nowISO = new Date().toISOString()) {
  const to = toTime(nowISO) ?? Date.now();
  const from = toTime(anchor?.at);
  return {
    from: new Date(from != null && from <= to ? from : to).toISOString(),
    to: new Date(to).toISOString(),
  };
}

export function resolveExactSaasVersionForProgram(versions = [], activeProgram = null) {
  if (!Number.isInteger(activeProgram?.published_version) || !activeProgram?.published_at) return null;
  const publishedAt = toTime(activeProgram.published_at);
  if (publishedAt == null) return null;
  return (Array.isArray(versions) ? versions : []).find((version) => {
    const publication = version?.client_app_publication || version?.clientAppPublication;
    return publication?.published_version === activeProgram.published_version
      && toTime(publication?.published_at) === publishedAt;
  }) || null;
}

function currentProgramLabel(program) {
  if (!program || !Number.isInteger(program.published_version)) return null;
  const activeAt = toIso(program.effective_at || program.published_at);
  return {
    value: `V${program.published_version}`,
    detail: activeAt ? `active depuis le ${formatDay(toTime(activeAt))}` : 'version active',
  };
}

function nextReviewSummary(cycleReviews, now) {
  const row = (Array.isArray(cycleReviews) ? cycleReviews : [])
    .map((review) => ({
      review,
      time: toTime(review?.scheduled_at || review?.visible_from || review?.due_at),
    }))
    .filter(({ review, time }) => time != null && time > now && review?.status !== 'submitted')
    .sort((a, b) => a.time - b.time)[0];
  if (!row) return null;
  const days = Math.max(1, Math.ceil((row.time - now) / DAY_MS));
  return {
    value: row.review?.cycle_label || `S${row.review?.step_number || ''}`,
    detail: `dans ${days} jour${days > 1 ? 's' : ''}`,
    at: new Date(row.time).toISOString(),
  };
}

export function buildLongitudinalSummary({
  anchor,
  nowISO = new Date().toISOString(),
  feedbacks = [],
  feedbackStatus = 'ok',
  meals = null,
  mealsStatus = 'ok',
  messages = [],
  messagesStatus = 'ok',
  activeProgram = null,
  cycleReviews = [],
} = {}) {
  const period = buildLongitudinalPeriod(anchor, nowISO);
  const from = toTime(period.from);
  const to = toTime(period.to);
  const windowFeedbacks = (Array.isArray(feedbacks) ? feedbacks : [])
    .filter((row) => inWindow(feedbackEventAt(row), from, to))
    .map((row) => ({ ...row, date: feedbackEventAt(row) }));
  const briefing = feedbackStatus === 'ok'
    ? buildBriefingData({ feedbacks: windowFeedbacks, weightEntries: [], sinceISO: period.from, nowISO: period.to })
    : null;
  const messageRows = (Array.isArray(messages) ? messages : [])
    .filter((row) => inWindow(row?.sent_at, from, to));
  const mealEvents = (Array.isArray(meals?.events) ? meals.events : [])
    .filter((row) => inWindow(row?.event_at || row?.set_at || row?.day, from, to));
  const mealTotals = mealEvents.reduce((totals, row) => {
    totals.recorded += 1;
    if (row.state === 'done') totals.done += 1;
    if (row.state === 'skip') totals.skipped += 1;
    if (row.state === 'replaced') totals.replaced += 1;
    if (!row.resolved_plan) totals.unresolved += 1;
    return totals;
  }, { recorded: 0, done: 0, skipped: 0, replaced: 0, unresolved: 0 });

  const cards = [];
  if (feedbackStatus !== 'ok') {
    cards.push({ id: 'feedback', label: 'Ressentis', status: 'unavailable', value: 'Indisponibles', detail: 'Synchronisation à réessayer' });
  } else {
    cards.push({
      id: 'feedback',
      label: 'Ressentis',
      status: 'ok',
      value: `${windowFeedbacks.length} jour${windowFeedbacks.length > 1 ? 's' : ''} renseigné${windowFeedbacks.length > 1 ? 's' : ''}`,
      detail: null,
    });
  }
  if (mealsStatus !== 'ok') {
    cards.push({ id: 'meals', label: 'Repas', status: 'unavailable', value: 'Indisponibles', detail: 'Synchronisation à réessayer' });
  } else {
    const details = [];
    if (mealTotals.replaced) details.push(`${mealTotals.replaced} remplacé${mealTotals.replaced > 1 ? 's' : ''}`);
    if (mealTotals.skipped) details.push(`${mealTotals.skipped} sauté${mealTotals.skipped > 1 ? 's' : ''}`);
    if (mealTotals.unresolved) details.push(`${mealTotals.unresolved} historique${mealTotals.unresolved > 1 ? 's' : ''} partiel${mealTotals.unresolved > 1 ? 's' : ''}`);
    cards.push({ id: 'meals', label: 'Repas', status: 'ok', value: `${mealTotals.recorded} renseigné${mealTotals.recorded > 1 ? 's' : ''}`, detail: details.join(' · ') || null });
  }
  if (messagesStatus !== 'ok') {
    cards.push({ id: 'messages', label: 'Échanges', status: 'unavailable', value: 'Indisponibles', detail: 'Synchronisation à réessayer' });
  } else {
    cards.push({ id: 'messages', label: 'Échanges', status: 'ok', value: `${messageRows.length} message${messageRows.length > 1 ? 's' : ''}`, detail: null });
  }
  const program = currentProgramLabel(activeProgram);
  if (program) cards.push({ id: 'program', label: 'Programme', status: 'ok', ...program });
  const nextReview = nextReviewSummary(cycleReviews, to);
  if (nextReview) cards.push({ id: 'review', label: 'Prochain bilan', status: 'ok', ...nextReview });

  const attention = [];
  if (mealTotals.replaced) attention.push(`${mealTotals.replaced} repas remplacé${mealTotals.replaced > 1 ? 's' : ''}`);
  if (mealTotals.skipped) attention.push(`${mealTotals.skipped} repas sauté${mealTotals.skipped > 1 ? 's' : ''}`);
  if (nextReview && (toTime(nextReview.at) - to) <= (7 * DAY_MS)) attention.push(`${nextReview.value} bientôt attendu`);

  return {
    anchor,
    period,
    durationDays: Math.max(1, Math.ceil((to - from) / DAY_MS)),
    cards: cards.slice(0, 6),
    attention,
    feedback: briefing,
    mealTotals,
    messageCount: messageRows.length,
  };
}

const decisionLabels = {
  continue: 'Décision : poursuivre le programme',
  adapt: 'Décision : adapter le programme',
  transition: 'Décision : préparer la phase suivante',
  newVersion: 'Décision : préparer une nouvelle version',
};

function groupByWeek(rows, readDate) {
  const buckets = new Map();
  for (const row of rows) {
    const time = toTime(readDate(row));
    if (time == null) continue;
    const week = mondayStart(time);
    const bucket = buckets.get(week) || { rows: [], latest: time };
    bucket.rows.push(row);
    bucket.latest = Math.max(bucket.latest, time);
    buckets.set(week, bucket);
  }
  return buckets;
}

export function buildLongitudinalEvents({
  anchor,
  nowISO = new Date().toISOString(),
  consultationsLog = [],
  cycleReviews = [],
  versions = [],
  feedbacks = [],
  meals = null,
  messages = [],
} = {}) {
  const period = buildLongitudinalPeriod(anchor, nowISO);
  const from = toTime(period.from);
  const to = toTime(period.to);
  const events = [];
  const linkedReviewProofs = [];

  (Array.isArray(consultationsLog) ? consultationsLog : []).forEach((row, index) => {
    const time = eventTime(row);
    if (time == null || time < from || time > to) return;
    const reviewed = readReviewedAppointment(row);
    const proof = reviewed?.cycleProof || null;
    if (proof) linkedReviewProofs.push(proof);
    const decision = reviewed ? (decisionLabels[reviewed.workflowAction] || null) : null;
    events.push({
      id: `consultation-${row?.id || index}`,
      kind: 'consultation',
      ts: time,
      label: `Consultation n°${index + 1}`,
      sub: [proof ? `liée au bilan S${proof.step_number}` : null, decision].filter(Boolean).join(' · ') || null,
      source: row,
    });
  });

  (Array.isArray(versions) ? versions : []).forEach((row, index) => {
    const publishedAt = row?.client_app_publication?.published_at || row?.published_at;
    const time = toTime(publishedAt);
    if (time == null || time < from || time > to) return;
    const version = row?.published_version || row?.version || ((versions?.length || 0) - index);
    events.push({ id: `publication-${row?.id || index}`, kind: 'publication', ts: time, label: `Programme V${version} publié`, source: row });
  });

  (Array.isArray(cycleReviews) ? cycleReviews : []).forEach((row) => {
    if (!row?.id || linkedReviewProofs.some((proof) => sameCycleReview(row, proof))) return;
    const date = row.status === 'submitted' ? row.submitted_at : (row.created_at || row.scheduled_at);
    const time = toTime(date);
    if (time == null || time < from || time > to) return;
    events.push({
      id: `cycle-review-${row.id}`,
      kind: 'cycle-review',
      ts: time,
      label: `${row.cycle_label || `Bilan S${row.step_number || ''}`} ${row.status === 'submitted' ? 'reçu' : 'envoyé'}`,
      source: row,
    });
  });

  groupByWeek(
    (Array.isArray(feedbacks) ? feedbacks : []).filter((row) => inWindow(feedbackEventAt(row), from, to)),
    feedbackEventAt,
  ).forEach((bucket, week) => {
    events.push({ id: `feedback-${week}`, kind: 'feedback', ts: bucket.latest, label: `${bucket.rows.length} ressenti${bucket.rows.length > 1 ? 's' : ''} renseigné${bucket.rows.length > 1 ? 's' : ''}`, sub: `semaine du ${formatDay(week)}` });
  });

  groupByWeek(
    (Array.isArray(meals?.events) ? meals.events : []).filter((row) => inWindow(row?.event_at || row?.set_at || row?.day, from, to)),
    (row) => row?.event_at || row?.set_at || row?.day,
  ).forEach((bucket, week) => {
    const replaced = bucket.rows.filter((row) => row.state === 'replaced').length;
    const skipped = bucket.rows.filter((row) => row.state === 'skip').length;
    const partial = bucket.rows.filter((row) => !row.resolved_plan).length;
    const details = [];
    if (replaced) details.push(`${replaced} remplacé${replaced > 1 ? 's' : ''}`);
    if (skipped) details.push(`${skipped} sauté${skipped > 1 ? 's' : ''}`);
    if (partial) details.push(`${partial} version${partial > 1 ? 's' : ''} non résolue${partial > 1 ? 's' : ''}`);
    events.push({ id: `meals-${week}`, kind: 'meals', ts: bucket.latest, label: `${bucket.rows.length} repas renseigné${bucket.rows.length > 1 ? 's' : ''}`, sub: [details.join(' · '), `semaine du ${formatDay(week)}`].filter(Boolean).join(' · ') });
  });

  groupByWeek(
    (Array.isArray(messages) ? messages : []).filter((row) => inWindow(row?.sent_at, from, to)),
    (row) => row?.sent_at,
  ).forEach((bucket, week) => {
    events.push({ id: `messages-${week}`, kind: 'messages', ts: bucket.latest, label: `${bucket.rows.length} échange${bucket.rows.length > 1 ? 's' : ''} avec Anissa`, sub: `semaine du ${formatDay(week)}` });
  });

  const rank = { consultation: 6, publication: 5, 'cycle-review': 4, messages: 3, feedback: 2, meals: 1 };
  return events.sort((a, b) => (b.ts - a.ts) || ((rank[b.kind] || 0) - (rank[a.kind] || 0)) || a.id.localeCompare(b.id));
}
